import {
  PRIORITY_REQUIRED_MESSAGE,
  TICKET_PRIORITY_LABELS,
  isReplyBlockedByPriorityRule,
  type TicketDetail,
  type TicketPriority,
} from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useQueueSettings } from '../../api/queue-settings';
import { useSetTicketPriority } from '../../api/tickets';
import { PRIORITY_TONES, PriorityChip, type PriorityTone } from '../../components/ticket';
import { DetailSection } from './DetailSection';

/** Livelli nell'ordine del prototipo, dal meno al più urgente. Solo etichette: i criteri sono TBD (PRD-004). */
const PRIORITY_OPTIONS: readonly TicketPriority[] = ['bassa', 'media', 'alta', 'urgente'];

/**
 * Il pulsante scelto riprende i colori del chip del suo livello (#118), con il bordo nel colore del
 * testo perché la selezione resti evidente anche su un fondo chiaro. Il passaggio del mouse non lo
 * cambia: cliccare il livello già scelto non fa nulla.
 */
const selectedSx = ({ bgcolor, color }: PriorityTone) => {
  const selected = { bgcolor, color, borderColor: color };
  return { '&.Mui-selected': { ...selected, '&:hover': selected } };
};

/**
 * Priorità del ticket (PRD-004 US-2, story #3): chi lavora il ticket la sceglie fra i quattro
 * livelli; senza è «Da triare». Non si rimuove: cliccare il livello scelto non fa nulla. Con la
 * regola «Priorità obbligatoria prima di rispondere» attiva (Impostazioni › Coda) un ticket da
 * triare mostra l'avviso: il composer di #5 non potrà inviare la risposta finché manca. Il pannello
 * lo monta con `key` = codice, così lo stato del salvataggio non passa da un ticket all'altro.
 */
export function TicketPrioritySection({ ticket }: { ticket: Pick<TicketDetail, 'code' | 'priority'> }) {
  const setPriority = useSetTicketPriority(ticket.code);
  const { data: settings } = useQueueSettings();
  // La regola si legge al momento: finché le impostazioni non arrivano l'avviso non compare.
  const replyBlocked = settings !== undefined && isReplyBlockedByPriorityRule(ticket, settings);

  // Durante il salvataggio i pulsanti restano attivi (disattivarli farebbe perdere il focus da
  // tastiera): il gruppo si dichiara occupato e gli altri clic sono ignorati.
  const choose = (_event: unknown, value: TicketPriority | null) => {
    if (setPriority.isPending || value === null || value === ticket.priority) return;
    setPriority.mutate(value);
  };

  return (
    <DetailSection title="Priorità">
      <Stack direction="row" spacing={3} alignItems="center">
        <PriorityChip priority={ticket.priority} />
        <Typography variant="body2" color="text.secondary">
          Assegnata dal supporto. Chi ha segnalato non la vede.
        </Typography>
      </Stack>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={ticket.priority}
        onChange={choose}
        aria-busy={setPriority.isPending}
        aria-label="Imposta la priorità"
      >
        {PRIORITY_OPTIONS.map((priority) => (
          <ToggleButton key={priority} value={priority} sx={{ px: 4, ...selectedSx(PRIORITY_TONES[priority]) }}>
            {TICKET_PRIORITY_LABELS[priority]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {replyBlocked && (
        <Alert severity="warning" role="note">
          {PRIORITY_REQUIRED_MESSAGE}
        </Alert>
      )}
      {setPriority.isError && (
        <Alert severity="error">
          <AlertTitle>Priorità non aggiornata</AlertTitle>
          {setPriority.error.message}
        </Alert>
      )}
    </DetailSection>
  );
}
